import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import { AccountAutoComplete } from '@/components';
import { SemenAutoComplete, SireAutoComplete } from '@/components';
import { StorageLocationAutoComplete } from '@/components';
import { AdvancedSemenLookup } from '@/components/common/advanced-semen-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown';
import { StringMapDropdown } from '@/components/common/string-map-dropdown';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useAccessToken } from '@/services/auth';
import {
  donorSchedulesByIdsQueryOptions,
  useBulkRevisionUpdate,
} from '@/services/chute-scheduler';

import {
  applySemenSelection,
  applySireSelection,
  BulkRevisionFormState,
  getModifiedFields,
  mergeFieldValues,
  SemenSelectionData,
} from './utils';

type MultipleValueFields = Set<string>;

interface BulkRevisionModalProps {
  visible: boolean;
  onHide: () => void;
  donorScheduleIds: string[];
  onSaveSuccess?: () => void;
}

export const BulkRevisionModal: React.FC<BulkRevisionModalProps> = ({
  visible,
  onHide,
  donorScheduleIds,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = useAccessToken();
  const [formState, setFormState] = useState<BulkRevisionFormState | null>(
    null,
  );
  const [initialState, setInitialState] =
    useState<BulkRevisionFormState | null>(null);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [multipleValueFields, setMultipleValueFields] =
    useState<MultipleValueFields>(new Set());
  const [backupSemenInputValue, setBackupSemenInputValue] = useState('');

  const {
    data: donorSchedules,
    isLoading,
    isError,
  } = useQuery({
    ...donorSchedulesByIdsQueryOptions(token, donorScheduleIds),
    enabled: !!token && visible && donorScheduleIds.length > 0,
  });

  const bulkUpdateMutation = useBulkRevisionUpdate();

  useEffect(() => {
    if (visible && donorSchedules && donorSchedules.length > 0) {
      const { formState: merged, multipleValueFields: mvFields } =
        mergeFieldValues(donorSchedules);
      setFormState(merged);
      setInitialState(merged);
      setTouchedFields(new Set());
      setMultipleValueFields(mvFields);
      setBackupSemenInputValue(merged.backupSemenName ?? '');
    }
  }, [donorSchedules, visible]);

  useEffect(() => {
    if (!visible) {
      setFormState(null);
      setInitialState(null);
      setTouchedFields(new Set());
      setMultipleValueFields(new Set());
      setBackupSemenInputValue('');
    }
  }, [visible]);

  const markTouched = useCallback((field: string) => {
    setTouchedFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }, []);

  const updateField = useCallback(
    (updates: Partial<BulkRevisionFormState>) => {
      setFormState((prev) => {
        if (!prev) return prev;
        return { ...prev, ...updates };
      });
      for (const key of Object.keys(updates)) {
        markTouched(key);
      }
    },
    [markTouched],
  );

  const handleSemenChange = useCallback(
    (semen: SemenSelectionData | null) => {
      if (!semen) {
        updateField({
          semenId: null,
          semenName: null,
          cane: null,
          colCode: null,
          naab: null,
          sireId: null,
          sireName: null,
          semenType: null,
          semenLocationId: null,
          semenLocationName: null,
          semenOwnerId: null,
          semenOwnerName: null,
        });
        return;
      }
      const updates = applySemenSelection(semen, formState!);
      updateField(updates);
    },
    [updateField, formState],
  );

  const handleSireChange = useCallback(
    (sire: { id: string; name: string } | null) => {
      if (!sire) {
        updateField({ sireId: null, sireName: null });
        return;
      }
      const updates = applySireSelection(sire, formState!);
      updateField(updates);
    },
    [updateField, formState],
  );

  const handleBackupSemenSelect = useCallback(
    (e: AutoCompleteSelectEvent) => {
      updateField({
        backupSemenId: e.value?.id ?? null,
        backupSemenName: e.value?.name ?? null,
      });
    },
    [updateField],
  );

  const handleSave = useCallback(async () => {
    if (!formState || !initialState || !donorSchedules) return;

    const modified = getModifiedFields(touchedFields, formState);

    const modifiedFieldNames = Object.keys(modified).filter(
      (k) => k !== 'donorScheduleIds',
    );

    if (modifiedFieldNames.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('common.noChanges'),
          detail: t('chuteScheduler.noFieldsModified'),
          life: ToastLife.MEDIUM,
        }),
      );
      return;
    }

    try {
      await bulkUpdateMutation.mutateAsync({
        donorScheduleIds,
        modifiedFields: modifiedFieldNames,
        ...modified,
      });

      dispatch(
        addToast({
          severity: 'success',
          summary: t('chuteScheduler.bulkRevisionSaved'),
          detail: t('chuteScheduler.donorSchedulesUpdated', {
            count: donorScheduleIds.length,
          }),
          life: ToastLife.MEDIUM,
        }),
      );

      onHide();
      onSaveSuccess?.();
    } catch {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('chuteScheduler.failedToSaveBulkRevision'),
          life: ToastLife.LONG,
        }),
      );
    }
  }, [
    formState,
    initialState,
    donorSchedules,
    touchedFields,
    donorScheduleIds,
    bulkUpdateMutation,
    dispatch,
    onHide,
    onSaveSuccess,
  ]);

  const getPlaceholder = useCallback(
    (field: string): string | undefined => {
      if (multipleValueFields.has(field)) return t('common.multipleValues');
      return undefined;
    },
    [multipleValueFields, t],
  );

  const renderFooter = () => (
    <div className="flex w-full items-center justify-end gap-2">
      <Button
        label={t('common.cancel')}
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
      />
      <Button
        label={t('common.save')}
        icon="pi pi-check"
        onClick={handleSave}
        loading={bulkUpdateMutation.isPending}
        autoFocus
      />
    </div>
  );

  const renderSemenTab = () => {
    if (!formState) return null;

    return (
      <div className="grid w-full grid-cols-2 gap-6">
        <div className="label-input-group">
          <div className="flex items-end gap-2">
            <AdvancedSemenLookup
              className="general-semen-lookup w-full"
              label={t('semen.semen')}
              value={
                formState.semenId
                  ? { id: formState.semenId, name: formState.semenName ?? '' }
                  : null
              }
              onChange={handleSemenChange}
              tabIndex={1}
              disabled={false}
            />
            <Button
              type="button"
              icon="pi pi-times"
              severity="secondary"
              text
              disabled={!formState.semenId}
              tabIndex={-1}
              onClick={() =>
                updateField({
                  semenId: null,
                  semenName: null,
                  cane: null,
                  colCode: null,
                  naab: null,
                  sireId: null,
                  sireName: null,
                  semenType: null,
                  semenLocationId: null,
                  semenLocationName: null,
                  semenOwnerId: null,
                  semenOwnerName: null,
                })
              }
            />
          </div>
        </div>

        <div className="label-input-group">
          <label>{t('semen.semenComment')}</label>
          <InputText
            value={formState.semenComment ?? ''}
            placeholder={
              formState.semenComment === null
                ? getPlaceholder('semenComment')
                : undefined
            }
            onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateField({ semenComment: e.target.value })
            }
            tabIndex={2}
          />
        </div>

        <div className="label-input-group">
          <label>{t('semen.caneCode')}</label>
          <InputText
            value={formState.cane ?? ''}
            placeholder={
              formState.cane === null ? getPlaceholder('cane') : undefined
            }
            tabIndex={3}
            className="w-full"
            disabled
          />
        </div>

        <div className="label-input-group">
          <StorageLocationAutoComplete
            key={`semenLoc-${formState.semenLocationId ?? 'empty'}`}
            label={t('semen.semenLocation')}
            defaultValue={
              formState.semenLocationId
                ? {
                    id: formState.semenLocationId,
                    name: formState.semenLocationName ?? '',
                  }
                : undefined
            }
            field="name"
            onSelect={(e: AutoCompleteSelectEvent) =>
              updateField({
                semenLocationId: e.value?.id ?? null,
                semenLocationName: e.value?.name ?? null,
              })
            }
            tabIndex={4}
          />
        </div>

        <div className="label-input-group">
          <label>{t('semen.colCode')}</label>
          <InputText
            value={formState.colCode ?? ''}
            placeholder={
              formState.colCode === null ? getPlaceholder('colCode') : undefined
            }
            className="w-full"
            tabIndex={5}
            disabled
          />
        </div>

        <div className="label-input-group">
          <AccountAutoComplete
            key={`semenOwner-${formState.semenOwnerId ?? 'empty'}`}
            label={t('semen.semenOwner')}
            defaultValue={
              formState.semenOwnerId
                ? {
                    id: formState.semenOwnerId,
                    name: formState.semenOwnerName ?? '',
                  }
                : undefined
            }
            field="name"
            onSelect={(e: AutoCompleteSelectEvent) =>
              updateField({
                semenOwnerId: e.value?.id ?? null,
                semenOwnerName: e.value?.name ?? null,
              })
            }
            tabIndex={6}
          />
        </div>

        <div className="label-input-group">
          <label>{t('semen.naab')}</label>
          <InputText
            value={formState.naab ?? ''}
            placeholder={
              formState.naab === null ? getPlaceholder('naab') : undefined
            }
            tabIndex={7}
            className="w-full"
            disabled
          />
        </div>

        <div className="label-input-group">
          <SemenAutoComplete
            key={`backupSemen-${formState.backupSemenId ?? 'empty'}`}
            label={t('semen.backupSemen')}
            defaultValue={
              formState.backupSemenId
                ? {
                    id: formState.backupSemenId,
                    name: formState.backupSemenName ?? '',
                  }
                : undefined
            }
            field="name"
            onSelect={handleBackupSemenSelect}
            inputValue={backupSemenInputValue}
            setInputValue={setBackupSemenInputValue}
            tabIndex={8}
          />
        </div>

        <div className="label-input-group">
          <AdvancedSireLookup
            label={t('sire.sire')}
            value={
              formState.sireId
                ? { id: formState.sireId, name: formState.sireName ?? '' }
                : null
            }
            onChange={handleSireChange}
            tabIndex={9}
            disabled={false}
          />
        </div>

        <div className="label-input-group">
          <SireAutoComplete
            key={`backupSire-${formState.backupSireId ?? 'empty'}`}
            label={t('sire.backupSire')}
            defaultValue={
              formState.backupSireId
                ? {
                    id: formState.backupSireId,
                    name: formState.backupSireName ?? '',
                  }
                : undefined
            }
            field="name"
            onSelect={(e: AutoCompleteSelectEvent) =>
              updateField({
                backupSireId: e.value?.id ?? null,
                backupSireName: e.value?.name ?? null,
              })
            }
            tabIndex={10}
          />
        </div>

        <div className="label-input-group">
          <StringMapDropdown
            label={t('semen.semenType')}
            value={formState.semenType ?? undefined}
            objectTypeCode={StringMapObjectTypeCode.SEMEN_TYPE}
            attributeName={StringMapAttributeName.SEMEN_TYPE}
            onChange={(e: DropdownChangeEvent) =>
              updateField({ semenType: e.value })
            }
            tabIndex={11}
          />
        </div>

        <div className="label-input-group">
          <StringMapDropdown
            label={t('semen.backupSemenType')}
            value={formState.backupSemenType ?? undefined}
            objectTypeCode={StringMapObjectTypeCode.SEMEN_TYPE}
            attributeName={StringMapAttributeName.SEMEN_TYPE}
            onChange={(e: DropdownChangeEvent) =>
              updateField({ backupSemenType: e.value })
            }
            tabIndex={12}
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog
      header={t('chuteScheduler.bulkRevisionHeader', {
        count: donorScheduleIds.length,
      })}
      visible={visible}
      style={{ width: '60vw' }}
      onHide={onHide}
      footer={renderFooter()}
      className="bulk-revision-modal"
      modal
    >
      <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
        {t('revision.revisionMode')}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <i className="pi pi-spin pi-spinner mr-2 text-2xl" />
          <span>{t('chuteScheduler.loadingDonorScheduleData')}</span>
        </div>
      )}

      {isError && (
        <div className="p-4 text-red-500">
          {t('chuteScheduler.failedToLoadDonorScheduleData')}
        </div>
      )}

      {!isLoading &&
        !isError &&
        donorSchedules &&
        donorSchedules.length === 0 && (
          <div className="p-4 text-yellow-600">
            {t('chuteScheduler.noMatchingDonorSchedules')}
          </div>
        )}

      {!isLoading && !isError && formState && renderSemenTab()}
    </Dialog>
  );
};
